import axios from 'axios';
import i18n from 'i18n-js';
import { SET_PASS_SCAN, ADD_CART, DELETE_CART, FETCH_CARTS, SET_PICKING_SESSION, SET_IS_CART_VIEW, UPDATE_CART, UPDATE_CART_LOCAL, SET_ACTIVE_CART, SET_CART_ORDERS, SET_NEXT_PICK_ITEM, SET_TOTE_ASSIGNMENTS, SET_UNSCANNED_ITEMS, SET_IS_NOT_EMPTY_CART, SET_CART_ASSIGNMENT_DETAILS_PROMPT, SET_CART_ASSIGNMENT_TOAST, SET_CART_ASSIGNMENT_DETAILS, SET_ZONE_ACCESS_MODAL_FOR_CART, SET_NON_ASSIGNMENT_DETAILS, SET_PREVIOUS_NEXT_PICK_ITEMS, SET_SCANNED_ITEMS, SET_ACTIVE_TOTE, SET_NOT_FULL_FILLED_ITEMS, SET_PREVIOUS_SCAN_INFO, SET_RFO_NO_ORDERS, SET_SHOW_CART_READY_BANNER, SET_OUT_OF_STOCK_PROMPT, SET_CART_TOAST_MESSAGE } from '../constants';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { extractGtin14FromBarcode } from '../helpers/gs1Barcode';
import { checkPendingTote, setCurrentOrder, setCartScanComplete, setCurrentCartScanComplete, resetFocusedOrder } from './ScanToCartScanningActions';
import { isKitItem } from '../components/scanToCartScanning/utils/scanProgressUtils';
import { filterTotesWithPendingItems, findProductWithId, findProductWithOrderItemId, findToteWithPickedItems, generateLogsBasedOnSerialType, getEvent, getScannedOrderItem, getSerialEnabledType, getUpdatedScanLog, isNotFullFilledItem, updateToteAssignments } from '../components/scanToCartScanning/utils/scanningUtils';
import { SearchScanpackOrder } from './scanpackAction';
import audioService from '../services/AudioService';
import { getReversePickListSetting } from '../utils/reversePickListUtils';

export function fetchCarts(callback) {
  return async dispatch => {
    try {
      const access_token = await AsyncStorage.getItem('access_token');
      const url = await AsyncStorage.getItem('url');
      const response = await axios.get(`${url}/carts.json`, {
        headers: {Authorization: `Bearer ${access_token}`}
      });
      dispatch({
        type: FETCH_CARTS,
        payload: response.data
      });
      if (callback) {
        callback();
      }
    } catch (error) {
      console.error('Error fetching carts:', error);
      if (callback) {
        callback();
      }
    }
  };
}

export function fetchCart(cartId, callback, options = {}) {
  return async dispatch => {
    try {
      const access_token = await AsyncStorage.getItem('access_token');
      const url = await AsyncStorage.getItem('url');
      const response = await axios.get(`${url}/carts/${cartId}.json`, {
        headers: {Authorization: `Bearer ${access_token}`}
      });
      const cartData = response.data;
      let orders = extractOrders(cartData);
      if (typeof orders === 'string') {
        try {
          orders = JSON.parse(orders);
        } catch (e) {
          orders = [];
        }
      }
      if (!Array.isArray(orders)) {
        orders = [];
      }
      const unScannedItems = extractUnScannedItems(orders);
      const toteAssignments = extractToteAssignments(cartData);
      const hasNoOrders = orders.length === 0;
      const allOrdersScanned = unScannedItems.length === 0;
      let allTotesPrinted = true;
      if (!hasNoOrders) {
        allTotesPrinted = toteAssignments.length > 0
          ? toteAssignments.every(tote => tote.isPrinted === true)
          : orders.every(order => order.status === 'scanned');
      }
      const isCartComplete = hasNoOrders || (allOrdersScanned && allTotesPrinted);
      dispatch(handleCartData(cartData));
      if (isCartComplete && !options.skipCompletionCheck) {
        dispatch(setCurrentCartScanComplete(true));
      }
      if (callback) {
        callback();
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
      if (callback) {
        callback();
      }
    }
  };
}

export function printToteLabels(cartId, callBack) {
  return async dispatch => {
    try {
      const access_token = await AsyncStorage.getItem('access_token');
      const url = await AsyncStorage.getItem('url');
      const response = await axios({
        method: 'get',
        url: `${url}/carts/${cartId}/print_tote_labels.pdf`,
        headers: {
          Authorization: `Bearer ${access_token}`
        },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const pdfUrl = window.URL.createObjectURL(blob);
      window.open(pdfUrl);
      
      if (callBack) {
        callBack(true);
      }
    } catch (error) {
      console.error('Error fetching PDF:', error);
      if (callBack) {
        callBack(false);
      }
    }
  };
}

export function addCart(cartData) {
  return async dispatch => {
    try {
      const access_token = await AsyncStorage.getItem('access_token');
      const url = await AsyncStorage.getItem('url');
      const response = await axios.post(`${url}/carts.json`, cartData, {
        headers: {Authorization: `Bearer ${access_token}`}
      });
      dispatch({
        type: ADD_CART,
        payload: response.data
      });
    } catch (error) {
      console.error('Error creating carts:', error);
    }
  };
}

export function updateCart(cartId, cartData, callBack) {
  return async dispatch => {
    try {
      const access_token = await AsyncStorage.getItem('access_token');
      const url = await AsyncStorage.getItem('url');
      const response = await axios.patch(`${url}/carts/${cartId}.json`, cartData, {
        headers: {Authorization: `Bearer ${access_token}`}
      });
      dispatch({
        type: UPDATE_CART,
        payload: response.data
      });
    } catch (error) {
      let errorMessage = 'Error updating cart';
      errorMessage = error.response.data.error || error.request || error.message || 'Cart is in use by orders';
      console.error('Error updating cart:', error);
      callBack(cartId);
    }
  };
}

export function updateCartDataLocal(cartData) {
  return dispatch => {
    dispatch({
      type: UPDATE_CART_LOCAL,
      payload: cartData
    });
  };
}

export function deleteCart(cartId, callback) {
  return async dispatch => {
    try {
      const access_token = await AsyncStorage.getItem('access_token');
      const url = await AsyncStorage.getItem('url');
      await axios.delete(`${url}/carts/${cartId}.json`, {
        headers: {Authorization: `Bearer ${access_token}`}
      });

      dispatch({
        type: DELETE_CART,
        payload: cartId
      });

      if (callback) {
        callback('Cart Deleted');
      }
    } catch (error) {
      console.error('Error deleting cart:', error);
      if (callback) {
        callback(cartId);
      }
    }
  };
}

export function setIsCartView(isCartView) {
  return dispatch => {
    dispatch({
      type: SET_IS_CART_VIEW,
      payload: isCartView
    });
  };
}

export function setPreviousNextPickItems(items) {
  return dispatch => {
    dispatch({
      type: SET_PREVIOUS_NEXT_PICK_ITEMS,
      payload: items
    });
  };
}

export function getCartInProgress() {
  return async dispatch => {
    try {
      const access_token = await AsyncStorage.getItem('access_token');
      const url = await AsyncStorage.getItem('url');
      const response = await axios.get(`${url}/carts/get_cart_in_progress.json`, {
        headers: {Authorization: `Bearer ${access_token}`}
      });
      if(response?.data?.status === 'OK'){
        if(response?.data?.message.includes('and is in progress')){
          const payload = {
            isInProgress: true,
            data: response.data.cart
          };
          dispatch(setPickingSession(payload));
        }
      }
    } catch (error) {
      const noCartInProgress = [404, 422].includes(error?.response?.status);
      if (!noCartInProgress) {
        console.error('Error get cart in progress:', error);
      }
    }
  };
}

const ORDER_REMOVED_FROM_CART = 'order_removed_from_cart';
const ORDER_REMOVED_FALLBACK_MESSAGE = 'This order was removed from the cart. Put its items aside or back on the shelf.';

export function updateCartToteProgress(tote, status = 'picked', qty = 1) {
  return async (dispatch, getState) => {
    const cartId = getState().scanToCart?.activeCart?.id;
    try {
      const data = {
        id: tote.id,
        order_id: tote.order_id,
        cart_id: cartId,
        scanned_qty: qty,
        status: status
      };
      const access_token = await AsyncStorage.getItem('access_token');
      const url = await AsyncStorage.getItem('url');

      await axios.post(`${url}/carts/scan_item.json`, data, {
        headers: { Authorization: `Bearer ${access_token}` }
      });
      return {ok: true};
    } catch (error) {
      const response = error?.response;
      if (response?.status === 422 && response?.data?.error_code === ORDER_REMOVED_FROM_CART) {
        dispatch(setCartToastMessage(response.data.message || ORDER_REMOVED_FALLBACK_MESSAGE, {severity: 'error', sticky: true}));
        dispatch(setCartAssignmentToast(true));
        audioService.playErrorSound();
        const pending = getState().scanToCart?.previousNextPickItems || [];
        dispatch(setPreviousNextPickItems(pending.filter(entry => entry?.tote?.order_id !== tote.order_id)));
        if (cartId) {
          await dispatch(fetchCart(cartId, null, {skipCompletionCheck: false}));
        }
        return {ok: false, error, errorCode: ORDER_REMOVED_FROM_CART};
      }
      console.error('Error updating cart tote progress:', error);
      return {ok: false, error};
    }
  }
}
export function handleProductInstructionByPass(sku, id, name) {
  return async (dispatch, getState) => {
    try {
      const { userInfo } = getState().user;
      const data = {
        id: id,
        sku: sku
      };
      const access_token = await AsyncStorage.getItem('access_token');
      const url = await AsyncStorage.getItem('url');
      const response = await axios({
        method: 'post',
        url: `${url}/orders/save_by_passed_log.json`,
        headers: {Authorization: `Bearer ${access_token}`},
        data
      })
      const log = {
        ...data,
        name: name,
        event: 'by_passed'
      }
      //TODO update the logs to updated
    } catch (error) {
      console.error('Error updating cart tote progress:', error);
    }
  }
}
export async function requestReplenish(data, callBack) {
  try {
    const access_token = await AsyncStorage.getItem('access_token');
    const url = await AsyncStorage.getItem('url');
    const response = await axios({
      method: 'post',
      url: `${url}/scan_pack/request_stock_replenishment.json`,
      headers: {Authorization: `Bearer ${access_token}`},
      data
    });

    if (callBack) callBack();
    return response.data;
  } catch (error) {
    console.error('Error requesting stock replenishment:', error);
  }
}

export function updateOrderProgress(updatedItem,updatedTote, barcodeValue, isNotFulfilled= false){
  return async (dispatch, getState) => {
    try {
        const { isPassScan } = getState().scanToCartScanning;
        if (isPassScan) {
          dispatch({ type: SET_PASS_SCAN, payload: false });
        }
        //TODO need to handle the out of stock api call here
        //need to update the log for not full filled
        if (isNotFulfilled) {
          const event = getEvent(isPassScan, barcodeValue);
            const { activeCart } = getState().scanToCart;
            const payload = {
              order_item_id: updatedItem.order_item_id,
              product_id: updatedItem.product_id,
              option: 'scan_item',
              cart_id: activeCart?.cart_id,
              event_data: {
                event: event,
                input: isPassScan ? updatedItem.barcodes?.[0]?.barcode : barcodeValue,
                id: updatedTote.order_id,
                state: 'scanpack.rfp.default',
                on_ex: 'on GPX',
                is_kit: updatedTote.parent_product_id ? true : false,
                product_id: updatedTote.parent_product_id ? updatedItem.product_id : null
              }
            }
            dispatch(handleItemOutOfStock(payload));
            return
        }
        const { scanLogs,serialRecordInputOne,serialRecordInputTwo } = getState().scanToCartScanning;
        const serialEnabledType = getSerialEnabledType(updatedItem);
        const {cartOrders} = getState().scanToCart;
        const { userInfo } = getState().user;
        const currentOrder = cartOrders.find(order => order.id === updatedTote.order_id);
        const scanLog = getUpdatedScanLog(updatedItem,updatedTote,barcodeValue,currentOrder.increment_id, userInfo?.name, isPassScan);
        const logs = generateLogsBasedOnSerialType(
          serialEnabledType,
          updatedItem,
          updatedTote,
          barcodeValue,
          serialRecordInputOne,
          serialRecordInputTwo,
          scanLog
        );
        //TODO need to add a action to update the logs of redux state
        dispatch(updateScanLogs(logs));
    } catch (error) {
      console.error('Error updating order progress:', error);
    }
  };
}

let scanLogBuffer = [];
let isThrottled = false;
const THROTTLE_INTERVAL = 300;
export function updateScanLogs(scanLog) {
  return async dispatch => {
    const incomingLogs = Array.isArray(scanLog) ? scanLog : [scanLog];
    const validIncomingLogs = incomingLogs.filter(Boolean);

    if (validIncomingLogs.length > 0) {
      scanLogBuffer.push(...validIncomingLogs);
    }

    if (isThrottled) {
      return;
    }

    if (scanLogBuffer.length === 0) {
      return;
    }

    isThrottled = true;
    const access_token = await AsyncStorage.getItem('access_token');
    const url = await AsyncStorage.getItem('url');
    const logsToSend = scanLogBuffer.splice(0, scanLogBuffer.length);
    const dataToSend = {
      app: "app",
      data: logsToSend
    };
    try {
      await axios({
        method: 'post',
        url: `${url}/scan_pack/scan_pack_v2`,
        headers: { Authorization: `Bearer ${access_token}` },
        data: dataToSend
      });
    } catch (error) {
      scanLogBuffer = [...logsToSend, ...scanLogBuffer];
      console.log('error:', error);
    }


    setTimeout(() => {
      isThrottled = false;
      if (scanLogBuffer.length > 0) {
        dispatch(updateScanLogs([]));
      }
    }, THROTTLE_INTERVAL);
  };
}

export function ResetCartInProgress() {
  const payload = {
    isInProgress : false,
    data : null,
    errorMessage: null,
  }

  return dispatch => {
    dispatch({
      type: SET_PICKING_SESSION,
      payload: payload
    });
  };
}

export function ReSetCartProgress(cartNumber, callBack) {
  return async dispatch => {
    try {
      const data = {
        app:"app",
        scan_to_cart_enabled:true,
        input: "-"+cartNumber,
        cart_action: 'restart',
        state:"scanpack.rfo"
      };
      dispatch(SearchScanpackOrder(data, callBack))
    } catch (error) {
      console.log(error)
    }
  };
}

// Same restart the scanner sends, without the scanner's response handling.
export function resetCartAssignments(cartCode) {
  return async () => {
    try {
      const access_token = await AsyncStorage.getItem('access_token');
      const url = await AsyncStorage.getItem('url');
      const response = await axios.post(
        `${url}/scan_pack/scan_barcode.json`,
        {app: 'app', scan_to_cart_enabled: true, input: `-${cartCode}`, cart_action: 'restart', state: 'scanpack.rfo'},
        {headers: {Authorization: `Bearer ${access_token}`}}
      );
      const messages = response.data?.success_messages || [];
      if (messages.some(message => message.includes('has been reset successfully'))) {
        return {ok: true};
      }
      return {ok: false, error: response.data?.error_messages?.[0]};
    } catch (error) {
      return {ok: false, error: error?.response?.data?.error_messages?.[0]};
    }
  };
}

export function handleCartData(data) {
  return async (dispatch, getState) => {
    try {
      const previousCart = getState().scanToCart.activeCart;
      if (previousCart && data && previousCart.cart_id !== data.cart_id) {
        dispatch(resetFocusedOrder());
      }
      const orders = extractOrders(data);
      const ordersItems = extractUnScannedItems(orders);
      const notFulfilledList = extractNotFullFilledItems(orders);
      const scannedItems = extractScannedItems(orders);
      const toteAssignments = extractToteAssignments(data);
      dispatch(setActiveCart(data))
      dispatch(setCartOrders(orders))
      dispatch(setToteAssignments(toteAssignments))
      dispatch(setUnScannedItems(ordersItems));
      dispatch(setScannedItems(scannedItems));
      dispatch(setNotFullFilledItems(notFulfilledList));
      const {cartScanComplete} = getState().scanToCartScanning;
      const result = findToteForNextItem(ordersItems, toteAssignments);
      if(ordersItems.length == 0) {
        const pendingTotes = filterTotesWithPendingItems(toteAssignments)
        if(notFulfilledList.length > 0 && pendingTotes?.length === 0) {
          dispatch(setCartScanComplete(true));
          dispatch(setIsCartView(true));
          return;
        }
        dispatch(checkPendingTote())
        return
      }
      if (result) {
        const pendingTotes = filterTotesWithPendingItems(toteAssignments)
        if(pendingTotes.length > 0) {
          dispatch(setCartScanComplete(false));
          dispatch(checkPendingTote())
        }
        else{
          const {isCartView} = getState().scanToCart;
          const {item: suggestedItem} = result;
          dispatch(setCurrentOrder(getOrderById(orders, suggestedItem?.order_id)))
          dispatch(setNextPickItem(result));
          if(isCartView) {
            dispatch(setIsCartView(false));
          }
          if (cartScanComplete) {
            dispatch(setCartScanComplete(false));
          }
        }
      }
      else {
        //TODO handle  No items to pick case
      }
    } catch (error) {
      
    }
  }

}

export function setActiveCart(cartData) {
  return dispatch => {
    dispatch({
      type: SET_ACTIVE_CART,
      payload: cartData
    });
  };
}

export function setCartOrders(orders) {
  return dispatch => {
    dispatch({
      type: SET_CART_ORDERS,
      payload: orders
    });
  };
}

export function handleCartOrdersUpdate(updateOrder){
  return (dispatch, getState) => {
    const { cartOrders } = getState().scanToCart;
    const { item } = getState().scanToCart.nextPickItem;
    const {order_id} = item;
    const updatedOrders = cartOrders.map(order =>
      order.id === updateOrder.id ? updateOrder : order
    );
    dispatch(setCartOrders(updatedOrders));
    const currentOrder = updatedOrders.find(order => order.id === order_id);
    dispatch(setCurrentOrder(currentOrder));
  };
}

export function setNextPickItem(pickItem) {
  return dispatch => {
    dispatch({
      type: SET_NEXT_PICK_ITEM,
      payload: pickItem
    });
  };
}

export function setToteAssignments(assignments) {
  return dispatch => {
    dispatch({
      type: SET_TOTE_ASSIGNMENTS,
      payload: assignments
    });
  };
}

export function setUnScannedItems(items) {
  return dispatch => {
    dispatch({
      type: SET_UNSCANNED_ITEMS,
      payload: items
    });
  };
}

export function updateCartProductIdentifiers(productId, {barcodes, skus} = {}) {
  return (dispatch, getState) => {
    const {unScannedItems, nextPickItem, cartOrders} = getState().scanToCart;
    const validBarcodes = barcodes?.filter(b => b?.barcode?.toString().trim());
    const firstSku = skus?.find(s => s?.sku?.toString().trim())?.sku;
    const patchItem = item => {
      if (!item) return item;
      let patched = item;
      if (item.product_id === productId) {
        patched = {...item};
        if (validBarcodes) patched.barcodes = validBarcodes;
        if (firstSku) patched.sku = firstSku;
      }
      if (patched.child_items?.length) {
        patched = {...patched, child_items: patched.child_items.map(patchItem)};
      }
      return patched;
    };
    dispatch(setUnScannedItems((unScannedItems || []).map(patchItem)));
    if (nextPickItem?.item) {
      dispatch(setNextPickItem({...nextPickItem, item: patchItem(nextPickItem.item)}));
    }
    dispatch(setCartOrders((cartOrders || []).map(order => ({
      ...order,
      unscanned_items: (order.unscanned_items || []).map(patchItem)
    }))));
  };
}

export function addBarcodeToCartItem(item, barcodeText) {
  return async dispatch => {
    const input = barcodeText?.toString().trim();
    if (!item?.product_id || !input) {
      return {ok: false, error: i18n.t('scanToCart.addBarcodeFailed')};
    }
    const value = extractGtin14FromBarcode(input) || input;
    try {
      const access_token = await AsyncStorage.getItem('access_token');
      const url = await AsyncStorage.getItem('url');
      const {data} = await axios.post(
        `${url}/products/update_product_list.json`,
        {value, var: 'barcode', id: item.product_id},
        {headers: {Authorization: `Bearer ${access_token}`}}
      );
      if (!data?.status) {
        const error = data?.show_alias_popup
          ? i18n.t('scanToCart.barcodeAssignedToOtherProduct')
          : [].concat(data?.error_msg || i18n.t('scanToCart.addBarcodeFailed')).join(' ').replace(/<br\s*\/?>/gi, ' ');
        return {ok: false, error};
      }
      const newBarcode = {barcode: value, packing_count: 1, product_id: item.product_id};
      dispatch(updateCartProductIdentifiers(item.product_id, {
        barcodes: [...(item.barcodes || []), newBarcode]
      }));
      return {ok: true};
    } catch (error) {
      return {ok: false, error: i18n.t('scanToCart.addBarcodeFailed')};
    }
  };
}
export function setScannedItems(items) {
  return dispatch => {
    dispatch({
      type: SET_SCANNED_ITEMS,
      payload: items
    });
  };
}

export function setPickingSession(sessionData) {
  return dispatch => {
    dispatch({
      type: SET_PICKING_SESSION,
      payload: sessionData
    });
  };
}

export function changeSuggestedItem(productId, updateOrder = false,checkPendingTote= false) {
  return (dispatch, getState) => {
    const { unScannedItems, toteAssignments, cartOrders, nextPickItem,notFullFilledItems } = getState().scanToCart;
    const {currentOrder} = getState().scanToCartScanning;
    // const updatedItems = moveToNextItem(unScannedItems);
    if(checkPendingTote) {
      const tote = findToteWithPickedItems(toteAssignments, productId);
      if(tote) {
        const scannedItem = getScannedOrderItem(cartOrders, tote);
        if(scannedItem) {
          dispatch(setNextPickItem({
            item: scannedItem,
            tote
          }));
          return;
        }
      }
      dispatch(setIsCartView(false));
    }
    const currentItem = nextPickItem.item;
    const isNotFullFilled = isNotFullFilledItem(notFullFilledItems,currentItem);
    const result = findToteForNextItem(isNotFullFilled? notFullFilledItems : unScannedItems, toteAssignments, productId,nextPickItem.tote?.cart_tote_id, isNotFullFilled);
    if(!result) {
      return;
    }
    if(updateOrder && currentOrder?.id !== result.item.order_id) {
      dispatch(setCurrentOrder(getOrderById(cartOrders, result.item.order_id)));
    }
    if(updateOrder && !checkPendingTote) {
      dispatch(setIsCartView(false));
    }
    // dispatch(setUnScannedItems(updatedItems));
    dispatch(setNextPickItem(result));
  };
}

export function handleNextPickItem(updatedItem, updatedTote) {
  return (dispatch, getState) => {
    dispatch(handleToteAssignmentsUpdate(updatedTote));
    const { isCartView } = getState().scanToCart;

    const updatedNextPickItem = {
      item: updatedItem,
      tote: updatedTote
    };

    if (!isCartView) {
      dispatch(setIsCartView(true));
      dispatch(setNextPickItem(updatedNextPickItem));
      return;
    }
    if (updatedItem.qty_remaining === 0) {
      dispatch(changeSuggestedItem(updatedItem.product_id));
    } else {
      dispatch(setNextPickItem(updatedNextPickItem));
    }
  };
}

export function handleToteAssignmentsUpdate (updatedTote) {
  return (dispatch, getState) => {
    const { toteAssignments } = getState().scanToCart;
    const updatedToteAssignments = updateToteAssignments(toteAssignments, updatedTote);
    dispatch(setToteAssignments(updatedToteAssignments));
  };
}

export function setNotFullFilledItems (items = [] ) {
  return dispatch => {
    dispatch({
      type: SET_NOT_FULL_FILLED_ITEMS,
      payload: items
    });
  }
}

export function handleReScanNotFulfilledItems() {
  return (dispatch, getState) => {
    const { notFullFilledItems, toteAssignments, activeCart, cartOrders } = getState().scanToCart;

    const sortedNotFulfilledItems = sortUnScannedItems(notFullFilledItems);

    const nextItem = sortedNotFulfilledItems.find(item => item.skipped_qty > 0);

    if (!nextItem) {
      return;
    }

    const toteAssignment = toteAssignments.find(assignment =>
      assignment.order_item_id === nextItem.order_item_id &&
      assignment.product_id === nextItem.product_id &&
      assignment.item_qty !== assignment.item_scanned_qty
    );

    const nextPickData = {
      item: nextItem,
      tote: toteAssignment || null
    };
    dispatch(setNextPickItem(nextPickData));
    dispatch(setCurrentOrder(getOrderById(cartOrders, nextItem.order_id)));

    const updatedCart = { ...activeCart, isPicked: false };
    dispatch(setActiveCart(updatedCart));
    dispatch(setCartScanComplete(false));
    dispatch(setIsCartView(false));
  };
}

export function handleItemOutOfStock(data, callBack = null) {
  return async (dispatch) => {
    try {
      const access_token = await AsyncStorage.getItem('access_token');
      const url = await AsyncStorage.getItem('url');
      const response = await axios.post(
        `${url}/scan_pack/order_item_out_of_stock.json`,
        data,
        {
          headers: {Authorization: `Bearer ${access_token}`}
        }
      );
      if (!data.cart_id) {
        return;
      }
      if(callBack) {
        callBack();
      }
      if(response?.data.results?.cancelled_items?.length > 0) {
        await dispatch(handleCartData(response?.data.results?.cart));
        dispatch(handleRemovedItemFeedback(response.data.results.cancelled_items));
        return ;
      }
      if(response?.data.results?.cart){
        await dispatch(handleCartData(response.data.results.cart));
      }
    } catch (error) {
      if(callBack) {
        callBack();
      }
      console.log(error);
    }
  };
}

export function handleLeaveOrderAsServiceIssue(toteId, holdMode, callBack = null) {
  return async (dispatch, getState) => {
    try {
      const access_token = await AsyncStorage.getItem('access_token');
      const url = await AsyncStorage.getItem('url');
      const { activeCart } = getState().scanToCart;
      const response = await axios.post(
        `${url}/scan_pack/order_item_out_of_stock.json`,
        { option: 'leave_order', hold_mode: holdMode, cart_id: activeCart?.cart_id, tote_id: toteId },
        { headers: { Authorization: `Bearer ${access_token}` } }
      );
      const results = response?.data?.results;
      if (!results || results.error) {
        return { ok: false, error: results?.error || i18n.t('notFulfilledItems.holdFailed') };
      }
      const cart = results.cart;
      if (cart) {
        await dispatch(handleCartData(cart));
        dispatch(showCartToastAndCompleteIfDone(results.message));
      }
      if (callBack) callBack();
      return { ok: true };
    } catch (error) {
      return { ok: false, error: i18n.t('notFulfilledItems.holdFailed') };
    }
  };
}

export function handleBackorderItem(toteId, callBack = null) {
  return async (dispatch, getState) => {
    try {
      const access_token = await AsyncStorage.getItem('access_token');
      const url = await AsyncStorage.getItem('url');
      const { activeCart } = getState().scanToCart;
      const response = await axios.post(
        `${url}/scan_pack/order_item_out_of_stock.json`,
        { option: 'backorder_item', tote_id: toteId, cart_id: activeCart?.cart_id },
        { headers: { Authorization: `Bearer ${access_token}` } }
      );
      const results = response?.data?.results;
      if (!results || results.error) {
        return { ok: false, error: results?.error || i18n.t('notFulfilledItems.backorderFailed') };
      }
      const cart = results.cart;
      if (cart) {
        await dispatch(handleCartData(cart));
        dispatch(showCartToastAndCompleteIfDone(results.message));
      }
      if (callBack) callBack();
      return { ok: true };
    } catch (error) {
      return { ok: false, error: i18n.t('notFulfilledItems.backorderFailed') };
    }
  };
}

function showCartToastAndCompleteIfDone(message) {
  return (dispatch, getState) => {
    if (message) {
      dispatch(setCartToastMessage(message));
      dispatch(setCartAssignmentToast(true));
    }
    const { cartOrders, unScannedItems, toteAssignments } = getState().scanToCart;
    const hasNoOrders = cartOrders.length === 0;
    const allTotesPrinted = hasNoOrders ? true : (toteAssignments.length > 0 && toteAssignments.every(tote => tote.isPrinted === true));
    const isCartComplete = hasNoOrders || (unScannedItems.length === 0 && allTotesPrinted);
    if (isCartComplete) {
      dispatch(setCurrentCartScanComplete(true));
    }
  };
}

function handleRemovedItemFeedback(cancelledItems) {
  return dispatch => {
    const orderWasCancelled = cancelledItems?.some(item => item.order_cancelled);
    const message = orderWasCancelled
      ? 'Order cancelled — out of stock. Customer/manager notified.'
      : 'Out-of-stock item(s) removed from the order. Customer/manager notified.';
    dispatch(showCartToastAndCompleteIfDone(message));
  };
}

export function setCartAssignmentDetails(notAssignedOrders = [], assignedOrders = []) {
  const payload = {
    notAssignedOrders,
    assignedOrders
  };

  return dispatch => {
    dispatch({
      type: SET_CART_ASSIGNMENT_DETAILS,
      payload: payload
    });
  };
}

export function setCartAssignmentToast(show = false) {
  return dispatch => {
    dispatch({
      type: SET_CART_ASSIGNMENT_TOAST,
      payload: show
    });
  };
}

export function setCartAssignmentDetailsPrompt(show = false) {
  return dispatch => {
    dispatch({
      type: SET_CART_ASSIGNMENT_DETAILS_PROMPT,
      payload: show
    });
  };
}

export function setZoneAccessModalForCart(payload) {
  return dispatch => {
    dispatch({
      type: SET_ZONE_ACCESS_MODAL_FOR_CART,
      payload: { visible: false, cartId: null, cartName: null, scanBarcodePayload: null, ...payload }
    });
  };
}

export function setNonAssignmentDetails(details) {
  return dispatch => {
    dispatch({
      type: SET_NON_ASSIGNMENT_DETAILS,
      payload: details
    });
  };
}

export function setRfoNoOrders(show = false) {
  return dispatch => {
    dispatch({
      type: SET_RFO_NO_ORDERS,
      payload: show
    });
  };
}

export function setPreviousScanInfo(info) {
  return dispatch => {
    dispatch({
      type: SET_PREVIOUS_SCAN_INFO,
      payload: info
    });
  };
}

export function setShowCartReadyBanner(show = false) {
  return dispatch => {
    dispatch({
      type: SET_SHOW_CART_READY_BANNER,
      payload: show
    });
  };
}

export function setShowOutOfStockPrompt(show = false) {
  return dispatch => {
    dispatch({
      type: SET_OUT_OF_STOCK_PROMPT,
      payload: show
    });
  };
}

export function setCartToastMessage(message = null, options = null) {
  return dispatch => {
    dispatch({
      type: SET_CART_TOAST_MESSAGE,
      payload: message,
      options
    });
  };
}

export function resetScanToCartStates() {
  return dispatch => {
    dispatch(setActiveCart(null));
    dispatch(setCartOrders([]));
    dispatch(setNextPickItem(null));
    dispatch(setToteAssignments([]));
    dispatch(setUnScannedItems([]));
    dispatch(setScannedItems([]));
    dispatch(setIsCartView(false));
    dispatch(setPickingSession({isInProgress: false, data: null}));
    dispatch(setCartAssignmentDetails([], []));
    dispatch(setCartAssignmentToast(false));
    dispatch(setCartAssignmentDetailsPrompt(false));
    dispatch(setNonAssignmentDetails(null));
    dispatch(setPreviousNextPickItems([]));
    dispatch(setShowCartReadyBanner(false));
    dispatch(setShowOutOfStockPrompt(false));
    dispatch(setCartToastMessage(null));
  };
}

export function setActiveTote(toteId) {
  return (dispatch, getState) => {
    if (!toteId) {
      dispatch({
        type: SET_ACTIVE_TOTE,
        payload: null
      });
      return;
    }
    const { toteAssignments } = getState().scanToCart;
    const tote = toteAssignments.find(t => t?.cart_tote_id === toteId);
    if(tote) {
      dispatch({
        type: SET_ACTIVE_TOTE,
        payload: tote
      })
    }
  };
}

function extractOrders(cartData) {
  return cartData?.cart_orders || [];
}

export function getOrderById(orders, orderId) {
  return orders.find(order => order.id === orderId);
}

function extractUnScannedItems(orders) {
  const unScannedItems = [];
  orders.forEach(order => {
    order.unscanned_items?.forEach(item => {
      if (isKitItem(item)) {
        item.child_items?.forEach(childItem => {
          unScannedItems.push({
            ...childItem,
            order_id: order.id,
            increment_id: order.increment_id
          });
        });
      } else {
        unScannedItems.push({
          ...item,
          order_id: order.id,
          increment_id: order.increment_id
        });
      }
    });
  });

  const shouldReverse = getReversePickListSetting();
  return sortUnScannedItems(unScannedItems, shouldReverse);
}

function extractNotFullFilledItems(orders) {
  const notFullFilledItems = [];
  orders.forEach(order => {
    order?.not_fulfilled_items?.forEach(item => {
      notFullFilledItems.push({
        ...item,
        order_id: order.id,
        increment_id: order.increment_id
      });
    });
  });
  return sortUnScannedItems(notFullFilledItems);
}

function extractScannedItems(orders) {
  const scannedItems = [];

  orders.forEach(order => {
    order.scanned_items?.forEach(item => {
      scannedItems.push({
        ...item,
        order_id: order.id,
        increment_id: order.increment_id
      });
    });
    order.not_fulfilled_items?.forEach(item => {
      if (!isKitItem(item) && Number(item?.scanned_qty) > 0) {
        scannedItems.push({
          ...item,
          order_id: order.id,
          increment_id: order.increment_id
        });
      }
    });
  });

  return scannedItems
}

export function sortUnScannedItems(unScannedItems, shouldReverse = false) {
  const sortedItems = unScannedItems.map(item => {
    const location = typeof item.location === 'string' ? item.location : '';
    const hasLocation = location.trim() !== '';

    return {
      item,
      hasLocation,
      locationTokens: hasLocation ? locationSortTokens(location) : []
    };
  }).sort((a, b) => {
    const hasLocationA = a.hasLocation;
    const hasLocationB = b.hasLocation;

    // Items with no location come first
    if (!hasLocationA && hasLocationB) return -1;
    if (hasLocationA && !hasLocationB) return 1;

    // Both have no location - sort by SKU
    if (!hasLocationA && !hasLocationB) {
      return String(a.item.sku ?? '').localeCompare(String(b.item.sku ?? ''), undefined, { numeric: true });
    }

    // Both have location - sort by location
    if (hasLocationA && hasLocationB) {
      return compareLocationTokens(a.locationTokens, b.locationTokens);
    }

    return 0;
  });

  if (shouldReverse) {
    sortedItems.reverse();
  }

  sortedItems.forEach(({ item }, index) => {
    unScannedItems[index] = item;
  });

  return unScannedItems;
}

function compareLocationTokens(tokensA, tokensB) {
  const length = Math.max(tokensA.length, tokensB.length);

  for (let i = 0; i < length; i += 1) {
    const tokenA = tokensA[i];
    const tokenB = tokensB[i];
    if (!tokenA && tokenB) return -1;
    if (tokenA && !tokenB) return 1;
    if (tokenA[0] !== tokenB[0]) return tokenA[0] - tokenB[0];

    const valueCompare = compareLocationTokenValues(tokenA, tokenB);
    if (valueCompare !== 0) return valueCompare;
  }

  return 0;
}

function compareLocationTokenValues(tokenA, tokenB) {
  if (tokenA[0] === 1 && tokenB[0] === 1) {
    if (tokenA[1][0] !== tokenB[1][0]) return tokenA[1][0] - tokenB[1][0];
    if (tokenA[1][1] < tokenB[1][1]) return -1;
    if (tokenA[1][1] > tokenB[1][1]) return 1;
    return 0;
  }

  if (tokenA[0] === 2) {
    return tokenA[1].localeCompare(tokenB[1]);
  }

  if (tokenA[1] < tokenB[1]) return -1;
  if (tokenA[1] > tokenB[1]) return 1;
  return 0;
}

function locationSortTokens(location) {
  const normalized = String(location ?? '');
  const parts = normalized.match(/[^\p{L}0-9]|[0-9]+|\p{L}+/gu) || [];

  return parts.map(part => {
    if (/^[^\p{L}0-9]+$/u.test(part)) return [0, part];
    if (/^[0-9]+$/.test(part)) return [1, normalizedNumericToken(part)];
    return [2, part.toLowerCase()];
  });
}

function normalizedNumericToken(part) {
  const stripped = part.replace(/^0+/, '') || '0';
  return [stripped.length, stripped];
}

export function extractToteAssignments(cartData) {
  const toteAssignments = [];

  cartData?.cart_rows?.forEach(row => {
    row.cart_totes?.forEach(tote => {
      tote.assign_order_to_totes?.forEach(assignment => {
        toteAssignments.push({
          ...assignment,
          tote_id: tote.tote_id,
          row_name: row.row_name
        });
      });
    });
  });

  return toteAssignments;
}

function resolveKitChild(item) {
  if (!isKitItem(item)) {
    return item;
  }
  return {
    ...item.child_items.find(child => child.qty_remaining > 0),
    order_id: item.order_id
  };
}

function findOpenTote(toteAssignments, item) {
  return toteAssignments.find(assignment =>
    assignment.order_item_id === item.order_item_id &&
    assignment.product_id === item.product_id &&
    assignment.item_qty !== assignment.item_scanned_qty
  );
}

export function findToteForNextItem(sortedUnScannedItems, toteAssignments, productId, toteId,isNotFullFilled = false) {
  let nextItem = null;
  let nextTote = null;
  if(toteId && productId) {
    nextTote = findToteById(toteAssignments, toteId, productId);
    if(nextTote) {
      nextItem = findProductWithOrderItemId(productId, nextTote.order_item_id, sortedUnScannedItems);
      if(nextItem) {
        return {
          item: nextItem,
          tote: nextTote
        };
      }
    }
  }
  if (productId) {
    nextItem = findProductWithId(productId, sortedUnScannedItems);
    if (nextItem && (nextItem.qty_remaining <= 0 )) {
      nextItem = null;
    }
  }
  if (!nextItem) {
    const hasWorkLeft = isNotFullFilled ? item => item.skipped_qty > 0 : item => item.qty_remaining > 0;
    const pendingItems = sortedUnScannedItems.filter(hasWorkLeft).map(resolveKitChild);
    nextItem = pendingItems.find(item => findOpenTote(toteAssignments, item)) || pendingItems[0];
  }

  if (!nextItem) {
    return null;
  }

  const toteAssignment = findOpenTote(toteAssignments, nextItem);

  return {
    item: nextItem,
    tote: toteAssignment || null
  };
}

function findToteById(toteAssignments, toteId, productId) {
  return toteAssignments.find(tote => 
    tote.cart_tote_id === toteId && 
    tote.product_id === productId && 
    tote.item_scanned_qty !== tote.item_qty
  );
}

// Move to next item in the sorted unscanned items list
export const moveToNextItem = (items, idKey = 'product_id') => {
  if (items.length <= 1) return items;

  const result = [...items];
  const originalFirstId = result[0]?.[idKey];

  while (result.length > 1 && result[1]?.[idKey] === originalFirstId) {
    result.push(result.shift());
  }

  result.push(result.shift());
  return result;
};
